import React, { useState, useEffect, useCallback } from 'react';
import { Storage as StorageIcon } from '@mui/icons-material';
import StorageSection from './StorageSection';
import {
  Box,
  
  Paper,
  Typography,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  useTheme,
  CircularProgress,
  IconButton,
  InputAdornment,
  Collapse,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PrivacyTip as PrivacyIcon,
  Article as TermsIcon,
  ContactMail as ContactIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  SaveAlt as SaveIcon,
  Edit as EditIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Article as ArticleIcon,
  ContactMail as ContactMailIcon,
  Public as PublicIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  Work as WorkIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  Badge as BadgeIcon,
  Key as KeyIcon,
  PauseCircle as PauseCircleIcon,
  DeleteForever as DeleteForeverIcon,
  Warning as WarningIcon,
  Notifications as NotificationsIcon,
  Email as NotifEmailIcon,
  MergeType as MergeTypeIcon,
  CreditCard as CreditCardIcon,
  Bolt as BoltIcon,
  AddCard as AddCardIcon,
  AutoAwesome as AutoAwesomeIcon,
  AccountBalance as AccountBalanceIcon,
  Schedule as ScheduleIcon,
  VerifiedUser as VerifiedUserIcon,
  Palette as PaletteIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';

import { axiosService } from 'shared/helpers/axios';
import { useLocale } from 'shared/contexts/LocaleContext';
import { ColorModeContext } from 'shared/styles/enhancedTheme';
import { useUserContext } from 'shared/contexts/UserContext';
import { refreshUserData, clearUserData } from 'shared/contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import TermsOfServiceDialog from './TermsOfServiceDialog';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';
import PostActionsSettings from './PostActionsSettings';
import PaymentDialog from 'components/common/billing/PaymentDialog';
import CreditBalance from 'components/common/billing/CreditBalance';
import TransactionHistory from 'components/common/billing/TransactionHistory';
import useBillingBalance from 'components/common/billing/useBillingBalance';
import { getTransactions, getPendingPurchases } from 'components/common/billing/billingApi';
// import BackButton from 'components/common/buttons/BackButton';
import { OAUTH_PROVIDERS, getLinkOAuthUrl } from 'auth/config/oauth';
import MergeAccountDialog, { MERGE_DISMISSED_KEY } from 'auth/forms/MergeAccountDialog';
import BillingLegalSection from './BillingLegalDialogs';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';


const SidebarItem = ({ icon, text, active, onClick, badge }) => {
  const theme = useTheme();
  
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={onClick}
        selected={active}
        sx={{
          borderRadius: 1,
          py: 1.5,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={text}
          primaryTypographyProps={{
            fontWeight: active ? 600 : 400,
            color: active ? 'primary.main' : 'text.primary',
          }}
        />
        {badge && (
          <Chip 
            label={badge} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
        {active && <ChevronRightIcon sx={{ ml: 1, color: 'primary.main' }} />}
      </ListItemButton>
    </ListItem>
  );
};

const PrivacySection = () => {
  // const [privacySettings, setPrivacySettings] = useState({
  //   profileVisibility: 'public',
  //   emailVisibility: 'contacts',
  //   activityVisibility: 'public',
  //   showOnlineStatus: true,
  //   dataSharing: false,
  // });


    const [settings, setSettings] = useState({});
    const [_loading, setLoading] = useState(false);
    const [_error, setError] = useState(null);
    const [_success, setSuccess] = useState(false);
    const [savingField, setSavingField] = useState(null);
    


const privacyCategories = [
  {
    title: 'Basic Information',
    // the email address is never shown to other users (they write through Messages): no setting for it
    description: 'Control visibility of your basic profile details. Your email address is never shown to other users.',
    icon: <PeopleIcon />,
    fields: [
      { key: 'privacy_show_contact', label: 'Contact Information', description: 'Phone and additional contact details' },
    ]
  },
  {
    title: 'Professional Details',
    description: 'Manage who sees your professional information',
    icon: <GroupsIcon />,
    fields: [
      { key: 'privacy_show_affiliations', label: 'Organization Affiliations', description: 'Companies, universities, and organizations you\'re associated with' },
      // { key: 'privacy_show_stats', label: 'Professional Statistics', description: 'Your achievements, metrics, and statistics' },
    ],
    tabFields: [
      { key: 'tab_show_experience',   label: 'Experience' },
      { key: 'tab_show_publications', label: 'Publications & Patents' },
      { key: 'tab_show_projects',     label: 'Projects' },
      { key: 'tab_show_stats',        label: 'Stats' },
    ],
  },
  {
    title: 'Social Activity',
    description: 'Control visibility of your social interactions',
    icon: <PublicIcon />,
    fields: [
      { key: 'privacy_show_activity', label: 'Activity Feed', description: 'Your posts, comments, and interactions' },
      { key: 'privacy_show_following', label: 'Connections & Following', description: 'Your connections and the organisations you follow' },
    ]
  }
];



const privacyOptions = [
  { 
    value: 'everyone', 
    label: 'Everyone', 
    icon: <PublicIcon fontSize="small" />,
    color: 'success',
    description: 'Visible to all users'
  },
  { 
    value: 'connections', 
    label: 'My connections', 
    icon: <PeopleIcon fontSize="small" />,
    color: 'info',
    description: 'Only visible to your connections'
  },
  { 
    value: 'nobody', 
    label: 'Only Me', 
    icon: <LockIcon fontSize="small" />,
    color: 'error',
    description: 'Private - only visible to you'
  },
];


  const theme = useTheme();



  // const handlePrivacyChange = (field, value) => {
  //   setPrivacySettings(prev => ({ ...prev, [field]: value }));
  // };


  useEffect(() => {
    fetchPrivacySettings();
  }, []);

  const fetchPrivacySettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosService.get('/users/privacy-settings/');
      setSettings(response.data);
    } catch (err) {
      setError('Failed to load privacy settings. Please try again.');
      // console.error('Error fetching privacy settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (field, value) => {
    // Optimistically update UI
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setSuccess(false);
    setSavingField(field);
    
    // Auto-save to backend
    try {
      await axiosService.patch('/users/privacy-settings/', { [field]: value });
      setSuccess(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      // Revert on error
      setSettings(prev => ({
        ...prev,
        [field]: settings[field]
      }));
      setError('Failed to save privacy settings. Please try again.');
      // console.error('Error saving privacy settings:', err);
    } finally {
      setSavingField(null);
    }
  };


  const getPrivacyOption = (value) => {
    
  return privacyOptions.find(opt => opt.value === value) || privacyOptions[0];
  };


  // const privacyOptions = [
  //   { value: 'public', label: 'Public', icon: <VisibilityIcon /> },
  //   { value: 'contacts', label: 'Contacts Only', icon: <PeopleIcon /> },
  //   { value: 'private', label: 'Private', icon: <VisibilityOffIcon /> },
  // ];

  const PrivacyBadge = ({ value }) => {
    const option = getPrivacyOption(value);
    return (
      <Chip
        size="small"
        icon={option.icon}
        label={option.label}
        color={option.color}
        variant="outlined"
        sx={{ 
          fontWeight: 500,
          '& .MuiChip-icon': {
            opacity: 0.8,
          }
        }}
      />
    );
  };


  return (
    <Box   sx={{ mt: -7, mb: 2,

     }}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
               
               <Box >

                 <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                 <PrivacyIcon color="primary" />  Privacy Settings
                 </Typography>
                 <Typography variant="body1" color="text.secondary" paragraph>
                   Control who can see your profile information and activity
                 </Typography>
               </Box>
             </Box>

            <Stack spacing={2}>
              {privacyCategories.map((category, index) => (
                <Paper 
                  key={category.title}
                  elevation={0}
                  sx={{ 
                    p: 2.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 0 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}>
                      {category.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {category.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={0}>
                    {category.fields.map((field) => {
                      const currentValue = settings[field.key] || 'everyone';
                      
                      return (
                        <Box 
                          key={field.key}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 2,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.background.default, 0.8),
                            }
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500} gutterBottom>
                              {field.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {field.description}
                            </Typography>
                            <PrivacyBadge value={currentValue} />
                          </Box>
                          
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Visibility</InputLabel>
                            <Select
                              value={currentValue}
                              onChange={(e) => handleChange(field.key, e.target.value)}
                              label="Visibility"
                              disabled={savingField === field.key}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {savingField === field.key ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    getPrivacyOption(selected).icon
                                  )}
                                  <Typography variant="body2">
                                    {getPrivacyOption(selected).label}
                                  </Typography>
                                </Box>
                              )}
                            >
                              {privacyOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {option.icon}
                                    <Box>
                                      <Typography variant="body2">{option.label}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.description}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      );
                    })}
                  </Stack>

                  {/* Tab visibility toggles — only on the Professional Details card */}
                  {category.tabFields && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body1" fontWeight={500} color="text.primary" sx={{ px: 2, pb: 0.5 }}>
                        Profile Visibility
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
                        Choose which tabs are visible to other users on your public profile
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1 }}>
                        {category.tabFields.map((tf) => {
                          const isOn = settings[tf.key] !== false;
                          return (
                            <Box
                              key={tf.key}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 2, py: 1, borderRadius: 1.5, minWidth: 200, flex: '1 1 180px',
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>{tf.label}</Typography>
                              <Switch
                                size="small"
                                checked={isOn}
                                disabled={savingField === tf.key}
                                onChange={() => handleChange(tf.key, !isOn)}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Paper>
              ))}
              <PostActionsSettings value={settings.post_actions || {}} saving={savingField === 'post_actions'}
                onChange={(v) => handleChange('post_actions', v)} />

            </Stack>
    </Box>
  );
};

const AccountSection = () => {
  const { user, loading: userLoading } = useUserContext();
  const theme = useTheme();
  const navigate = useNavigate();

  // ---- Personal info state ---- //
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    contact_number: '',
    position: '',
    city: '',
    website: '',
    bio: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ---- Password state ---- //
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new_pw: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Populate form when user loads
  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        email: user.email || '',
        contact_number: user.contact_number || '',
        position: user.position || '',
        city: user.city || '',
        website: user.website || '',
        bio: user.bio || '',
      });
    }
  }, [user]);

  const TEXT_FIELDS = ['first_name', 'last_name', 'position', 'city', 'bio', 'username'];
  const isIntegerOnly = (val) => typeof val === 'string' && /^\d+$/.test(val.trim());

  const handleChange = (field, value) => {
    if (TEXT_FIELDS.includes(field) && isIntegerOnly(value)) return;
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  const handleCancel = () => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        email: user.email || '',
        contact_number: user.contact_number || '',
        position: user.position || '',
        city: user.city || '',
        website: user.website || '',
        bio: user.bio || '',
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    const invalidField = TEXT_FIELDS.find(f => isIntegerOnly(form[f]));
    if (invalidField) {
      setError(`"${invalidField.replace('_', ' ')}" cannot be a number only.`);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await axiosService.patch('/users/me/', form);
      await refreshUserData();
      setSuccess('Profile updated successfully.');
      setIsEditing(false);
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        const messages = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' · ');
        setError(messages);
      } else {
        setError('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Password change handler ---- //
  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!passwordForm.current_password) {
      setPasswordError('Current password is required.');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      await axiosService.post('/users/me/change-password/', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordSuccess('Password changed successfully.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        data?.detail || data?.error || data?.current_password?.[0] || data?.new_password?.[0]
        || 'Failed to change password.';
      setPasswordError(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: -7, mb: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* <BackButton position="inline" sx={{ mt: 0.5 }} /> */}
        <PersonIcon color="primary" /> Account Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your account information and preferences
      </Typography>

      {/* ───────── Personal Information ───────── */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user?.avatar && (
              <Avatar src={user.avatar} sx={{ width: 48, height: 48 }} />
            )}
            <Box>
              <Typography variant="h6">Personal Information</Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            )}
            <Button
              startIcon={isEditing ? (saving ? <CircularProgress size={18} /> : <SaveIcon />) : <EditIcon />}
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              variant={isEditing ? 'contained' : 'outlined'}
              disabled={saving}
            >
              {isEditing ? (saving ? 'Saving…' : 'Save Changes') : 'Edit'}
            </Button>
          </Box>
        </Box>

        <Collapse in={!!error}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Collapse>
        <Collapse in={!!success}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Collapse>

        <Stack spacing={2.5}>
          {/* Name row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="First Name"
              value={form.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><PersonIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={form.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              disabled={!isEditing}
            />
          </Stack>

          {/* Username & Email */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Username"
              value={form.username}
              onChange={(e) => handleChange('username', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><BadgeIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><EmailIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
          {/* </Stack> */}

          {/* Position & City */}
          {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Position / Title"
              value={form.position}
              onChange={(e) => handleChange('position', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><WorkIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="City"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><LocationOnIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
          </Stack> */}

          {/* Phone & Website */}
          {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Phone Number"
              value={form.contact_number}
              onChange={(e) => handleChange('contact_number', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><PhoneIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Website"
              value={form.website}
              onChange={(e) => handleChange('website', e.target.value)}
              disabled={!isEditing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><LinkIcon sx={{ opacity: 0.6 }} /></InputAdornment>
                ),
              }}
            />
          </Stack> */}

          {/* Bio */}
          {/* <TextField
            fullWidth
            label="Bio"
            value={form.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            disabled={!isEditing}
            multiline
            rows={3}
            placeholder={isEditing ? 'Tell us about yourself…' : ''}
          /> */}
        </Stack>
        </Stack>
        {/* </Paper> */}

      {/* ───────── Change Password ───────── */}
      {/* <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}> */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, mt:3 }}>
          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
            <KeyIcon />
          </Box>
          <Box>
            <Typography variant="h6">Change Password</Typography>
            <Typography variant="body2" color="text.secondary">
              Update your password to keep your account secure
            </Typography>
          </Box>
        </Box>

        <Collapse in={!!passwordError}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setPasswordError(null)}>
            {passwordError}
          </Alert>
        </Collapse>
        <Collapse in={!!passwordSuccess}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setPasswordSuccess(null)}>
            {passwordSuccess}
          </Alert>
        </Collapse>

        <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
          <TextField
            fullWidth
            label="Current Password"
            type={showPasswords.current ? 'text' : 'password'}
            value={passwordForm.current_password}
            onChange={(e) => {
              setPasswordForm(prev => ({ ...prev, current_password: e.target.value }));
              setPasswordError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon sx={{ opacity: 0.6 }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => togglePasswordVisibility('current')} edge="end" aria-label={showPasswords.current ? 'Hide current password' : 'Show current password'}>
                    {showPasswords.current ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showPasswords.new_pw ? 'text' : 'password'}
            value={passwordForm.new_password}
            onChange={(e) => {
              setPasswordForm(prev => ({ ...prev, new_password: e.target.value }));
              setPasswordError(null);
            }}
            helperText="Must be at least 8 characters"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon sx={{ opacity: 0.6 }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => togglePasswordVisibility('new_pw')} edge="end" aria-label={showPasswords.new_pw ? 'Hide new password' : 'Show new password'}>
                    {showPasswords.new_pw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={passwordForm.confirm_password}
            onChange={(e) => {
              setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }));
              setPasswordError(null);
            }}
            error={
              passwordForm.confirm_password.length > 0 &&
              passwordForm.new_password !== passwordForm.confirm_password
            }
            helperText={
              passwordForm.confirm_password.length > 0 &&
              passwordForm.new_password !== passwordForm.confirm_password
                ? 'Passwords do not match'
                : ' '
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon sx={{ opacity: 0.6 }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => togglePasswordVisibility('confirm')} edge="end" aria-label={showPasswords.confirm ? 'Hide confirm password' : 'Show confirm password'}>
                    {showPasswords.confirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={changingPassword ? <CircularProgress size={18} color="inherit" /> : <SecurityIcon />}
            onClick={handlePasswordChange}
            disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
            sx={{ alignSelf: 'flex-start', minWidth: 180 }}
          >
            {changingPassword ? 'Updating…' : 'Update Password'}
          </Button>
        </Stack>
      </Paper>

      {/* ───────── Danger Zone ───────── */}
      <Paper elevation={0} sx={{ p: 3, border: '2px solid', borderColor: 'error.main', borderRadius: 1, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
            <WarningIcon />
          </Box>
          <Box>
            <Typography variant="h6" color="error.main">Quit Otable.net</Typography>
            <Typography variant="body2" color="text.secondary">
              Deactivate or delete your account. Please read the options carefully before proceeding.
            </Typography>
          </Box>
        </Box>

        <DangerZoneActions navigate={navigate} />
      </Paper>
    </Box>
  );
};

const DangerZoneActions = ({ navigate }) => {
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleDeactivate = async () => {
    setError(null);
    if (!password) {
      setError('Password is required.');
      return;
    }
    setLoading(true);
    try {
      await axiosService.post('/users/me/deactivate/', { password });
      await axiosService.post('/auth/logout/').catch(() => {});
      await clearUserData();
      navigate('/login/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to deactivate account.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type DELETE to confirm.');
      return;
    }
    setLoading(true);
    try {
      await axiosService.post('/users/me/delete/', { password });
      await axiosService.post('/auth/logout/').catch(() => {});
      await clearUserData();
      navigate('/login/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete account.');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = (setter) => {
    setPassword('');
    setShowPassword(false);
    setError(null);
    setDeleteConfirmText('');
    setLoading(false);
    setter(false);
  };

  return (
    <>
      <Stack spacing={2}>
        {/* Deactivate */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Deactivate Account</Typography>
            <Typography variant="body2" color="text.secondary">
              Temporarily disable your account. Your account will automatically reactivate the next time you log in.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PauseCircleIcon />}
            onClick={() => setDeactivateOpen(true)}
          >
            Deactivate
          </Button>
        </Box>

        {/* Delete */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Delete Account</Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently delete your account and all associated data. This cannot be undone.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Stack>

      {/* Deactivate Dialog */}
      <Dialog open={deactivateOpen} onClose={() => resetAndClose(setDeactivateOpen)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PauseCircleIcon color="error" /> Deactivate Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your account will be disabled and you will be logged out. Your profile will no longer be visible to other users. You can reactivate it at any time by logging in again.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Confirm your password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => resetAndClose(setDeactivateOpen)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeactivate}
            disabled={loading || !password}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PauseCircleIcon />}
          >
            {loading ? 'Deactivating…' : 'Deactivate Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => resetAndClose(setDeleteOpen)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteForeverIcon /> Permanently Delete Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            This action is permanent and cannot be undone. All your data, posts, memberships, and consortiums will be permanently removed.
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Confirm your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null); }}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label='Type "DELETE" to confirm'
              value={deleteConfirmText}
              onChange={(e) => { setDeleteConfirmText(e.target.value); setError(null); }}
              error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
              helperText={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE' ? 'Must type DELETE exactly' : ' '}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => resetAndClose(setDeleteOpen)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading || !password || deleteConfirmText !== 'DELETE'}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
          >
            {loading ? 'Deleting…' : 'Delete Account Forever'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TermsSection = () => {
  const { user, refreshUser, isAuthenticated } = useUserContext();
  
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAcceptPrivacy = async (consentSettings) => {
    if (!isAuthenticated) return;
    
    setSaving(true);
    try {
      await axiosService.patch('/users/privacy-settings/', { 
        accepted_privacy_policy: true 
      });
      await refreshUser(); // Refresh user data from backend
      setPrivacyOpen(false);
      // console.log('Privacy policy accepted');
    } catch (err) {
      console.error('Error updating privacy acceptance:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptTerms = async (newState) => {
    if (!isAuthenticated) return;
    
    setSaving(true);
    try {
      await axiosService.patch('/users/privacy-settings/', { 
        accepted_terms_of_service: newState 
      });
      await refreshUser(); // Refresh user data from backend
      // console.log('Terms acceptance changed to:', newState);
      if (newState) {
        setTermsOpen(false);
      }
    } catch (err) {
      // console.error('Error updating terms acceptance:', err);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Box>
      <Typography variant="h5" 
              fontWeight={600} 
              gutterBottom 
              sx={{ display: 'flex', alignItems: 'center', 
                    gap: 1, mt: -7 }}>

{/* <BackButton 
position="inline" 
sx={{ mt: 0.5 }}
/> */}

        <ArticleIcon color="primary" /> Terms & Policies
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Review and manage your agreement to our terms and policies
      </Typography>

      <Stack spacing={3}>
        {/* Terms of Service */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>Terms of Service</Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated: June 1st, 2026
              </Typography>
            </Box>
            <Chip 
              label={user?.accepted_terms_of_service ? "Accepted" : "Not Accepted"} 
              color={user?.accepted_terms_of_service ? "success" : "default"}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" paragraph>
            These Terms of Service govern your use of our platform. Please read them carefully.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" 
              startIcon={<ArticleIcon />} 
              onClick={() => setTermsOpen(true)} >
              View Full Terms
            </Button>
          </Box>
        </Paper>

        {/* Privacy Policy */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>Privacy Policy</Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated: June 1st, 2026
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" paragraph>
            Learn how we collect, use, and protect your personal information.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" startIcon={<PrivacyIcon />} onClick={() => setPrivacyOpen(true)}>
              View Privacy Policy
            </Button>
          </Box>
        </Paper>

        {/* Cookie Policy */}
        {/* <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>Cookie Policy</Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated: January 5, 2024
              </Typography>
            </Box>
            <Chip 
              label={acceptedTerms.cookiePolicy ? "Accepted" : "Not Accepted"} 
              color={acceptedTerms.cookiePolicy ? "success" : "default"}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" paragraph>
            Understand how we use cookies and similar technologies on our platform.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" startIcon={<InfoIcon />}>
              View Cookie Policy
            </Button>
            <Button 
              variant="contained" 
              startIcon={<CheckCircleIcon />}
              onClick={() => setAcceptedTerms(prev => ({ ...prev, cookiePolicy: !prev.cookiePolicy }))}
            >
              {acceptedTerms.cookiePolicy ? 'Revoke Acceptance' : 'Accept Policy'}
            </Button>
          </Box>
        </Paper> */}
      </Stack>



      <TermsOfServiceDialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={handleAcceptTerms}
        isAccepted={user?.accepted_terms_of_service || false}
      />


      <PrivacyPolicyDialog
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        onAccept={handleAcceptPrivacy}
      />

    </Box>
  );
};

const REPORT_CATEGORIES = [
  { value: 'complaint', label: 'Complaint' },
  { value: 'request', label: 'Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'abuse', label: 'Abuse / Harassment' },
  { value: 'spam', label: 'Spam' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'copyright', label: 'Copyright Violation' },
  { value: 'privacy', label: 'Privacy Concern' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLORS = { pending: 'warning', reviewing: 'info', resolved: 'success', dismissed: 'default' };

const ContactSection = () => {
  const { user } = useUserContext();
  const theme = useTheme();

  // Report form state
  const [reportForm, setReportForm] = useState({
    category: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  // Previous reports
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const res = await axiosService.get('/interact/reports/');
      setReports(res.data?.results || res.data || []);
    } catch (err) {
      // console.error('Failed to load reports:', err);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!reportForm.category || !reportForm.subject || !reportForm.message) {
      setSubmitError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      await axiosService.post('/interact/reports/', reportForm);
      setSubmitSuccess('Report submitted successfully. We will review it shortly.');
      setReportForm({ category: '', subject: '', message: '' });
      fetchReports();
    } catch (err) {
      const data = err?.response?.data;
      const msg = typeof data === 'string' ? data
        : data?.detail || data?.message || 'Failed to submit report.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -7 }}>
        {/* <BackButton position="inline" sx={{ mt: 0.5 }} /> */}
        <ContactMailIcon color="primary" /> Contact & Support
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Submit a report, request, or complaint — our team will review and respond
      </Typography>

      <Stack spacing={3}>
        {/* ───────── Submit a Report ───────── */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
              <SendIcon />
            </Box>
            <Box>
              <Typography variant="h6">Submit a Report</Typography>
              <Typography variant="body2" color="text.secondary">
                Reports are prioritized by category and reviewed by our moderation team
              </Typography>
            </Box>
          </Box> */}

          <Collapse in={!!submitError}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          </Collapse>
          <Collapse in={!!submitSuccess}>
            <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setSubmitSuccess(null)}>
              {submitSuccess}
            </Alert>
          </Collapse>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>

              <Stack direction="row" alignItems="center" spacing={1}>
              {/* Category */}
              <FormControl sx={{ width: '60%' }} required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={reportForm.category}
                  onChange={(e) => {
                    setReportForm(prev => ({ ...prev, category: e.target.value }));
                    setSubmitError(null);
                  }}
                  label="Category"
                >
                  {REPORT_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Subject */}
              <TextField
                fullWidth
                label="Subject"
                value={reportForm.subject}
                onChange={(e) => {
                  setReportForm(prev => ({ ...prev, subject: e.target.value }));
                  setSubmitError(null);
                }}
                required
                placeholder="Brief summary of your report"
              />
            </Stack> 

              {/* Message */}
              <TextField
                fullWidth
                label="Message"
                value={reportForm.message}
                onChange={(e) => {
                  setReportForm(prev => ({ ...prev, message: e.target.value }));
                  setSubmitError(null);
                }}
                required
                multiline
                rows={4}
                placeholder="Describe the issue in detail…"
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                disabled={submitting}
                sx={{ alignSelf: 'flex-start', minWidth: 160 }}
              >
                {submitting ? 'Submitting…' : 'Submit Report'}
              </Button>
            </Stack>
          </form>
        </Paper>

        {/* ───────── My Reports ───────── */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>My Reports</Typography>

          {loadingReports ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : reports.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              You haven't submitted any reports yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {reports.map((report) => (
                <Paper
                  key={report.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    borderLeft: `4px solid`,
                    borderLeftColor: `${STATUS_COLORS[report.status] || 'grey'}.main`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="subtitle2">{report.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {report.category_display}
                        {report.target_type && ` · ${report.target_type}`}
                        {' · '}
                        {new Date(report.created).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Chip
                      label={report.status_display || report.status}
                      size="small"
                      color={STATUS_COLORS[report.status] || 'default'}
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  </Box>
                  {report.resolution_note && (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 1, py: 0.25 }}>
                      <Typography variant="body2"><strong>Resolution:</strong> {report.resolution_note}</Typography>
                    </Alert>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};


const EMAIL_NOTIFICATION_CATEGORIES = [
  {
    key: 'email_notify_invitations',
    label: 'Invitations',
    description: 'When you receive or respond to a consortium or collaboration invitation.',
    examples: ['Consortium invitation received', 'Your invitation was accepted / declined'],
  },
  {
    key: 'email_notify_consortium',
    label: 'Consortium Activity',
    description: 'Events inside consortia you belong to.',
    examples: ['Join request submitted or decided', 'Role assigned to you', 'Consortium profile updated'],
  },
  {
    key: 'email_notify_proposals',
    label: 'Proposals',
    description: 'Activity in proposals and work packages you are part of.',
    examples: ['Proposal updated', 'Team chat message', 'Task assigned', 'Re-invitation to proposal'],
  },
  {
    key: 'email_notify_meetings',
    label: 'Meetings',
    description: 'Meeting requests and scheduling polls sent to you.',
    examples: ['New meeting request', 'Poll updated'],
  },
  {
    key: 'email_notify_suggestions',
    label: 'Suggestions',
    description: 'When another researcher suggests a funding call, consortium, or researcher to you.',
    examples: ['Suggestion received'],
  },
  {
    key: 'email_notify_social',
    label: 'Social',
    description: 'Likes, follows, comments, and direct messages.',
    examples: ['Someone followed you', 'New like or comment', 'Direct message'],
  },
];

const NOTIF_DEFAULTS = {
  notify_by_email: false,
  email_notify_invitations: true,
  email_notify_consortium: true,
  email_notify_proposals: false,
  email_notify_meetings: true,
  email_notify_suggestions: true,
  email_notify_social: false,
  email_notify_calls: 'weekly',
};

const NotificationsSection = () => {
  const theme = useTheme();
  const [prefs, setPrefs] = useState(NOTIF_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key being saved, or 'all'
  const [error, setError] = useState(null);
  const [savedKey, setSavedKey] = useState(null); // briefly highlight saved row

  useEffect(() => {
    axiosService.get('/users/privacy-settings/')
      .then(res => setPrefs(prev => ({ ...prev, ...res.data })))
      .catch(() => setError('Could not load notification settings.'))
      .finally(() => setLoading(false));
  }, []);

  const patch = async (key, value) => {
    const prev = prefs[key];
    setPrefs(p => ({ ...p, [key]: value }));
    setSaving(key);
    setError(null);
    try {
      await axiosService.patch('/users/privacy-settings/', { [key]: value });
      setSavedKey(key);
      setTimeout(() => setSavedKey(k => k === key ? null : k), 1500);
    } catch {
      setPrefs(p => ({ ...p, [key]: prev }));
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(s => s === key ? null : s);
    }
  };

  const masterOn = !!prefs.notify_by_email;

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon color="primary" /> Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Otable always creates in-app notifications for your activity. Configure which of these are also delivered to your email inbox.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* ── Master switch ── */}
          <Paper elevation={0} sx={{
            p: 2.5, borderRadius: 1,
            border: '1px solid',
            borderColor: masterOn ? 'primary.main' : 'divider',
            bgcolor: masterOn ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
            transition: 'all 0.2s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex' }}>
                <NotifEmailIcon />
              </Box>
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight={600}>Email notifications</Typography>
                <Typography variant="body2" color="text.secondary">
                  Master switch — turn off to stop all notification emails regardless of category settings below.
                </Typography>
              </Box>
              {saving === 'notify_by_email' ? (
                <CircularProgress size={22} sx={{ flexShrink: 0 }} />
              ) : (
                <Switch
                  checked={masterOn}
                  onChange={e => patch('notify_by_email', e.target.checked)}
                  color="primary"
                />
              )}
            </Box>
          </Paper>

          {/* ── Per-category toggles ── */}
          <Paper elevation={0} sx={{
            borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
            opacity: masterOn ? 1 : 0.5,
            transition: 'opacity 0.2s',
            pointerEvents: masterOn ? 'auto' : 'none',
          }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(theme.palette.background.default, 0.6) }}>
              <Typography variant="overline" fontSize={"1.05rem"} color="text.secondary" fontWeight={600} 
                  sx={{ letterSpacing: 1 }}>
                Notify me by email for…
              </Typography>
            </Box>
            <Divider />
            {EMAIL_NOTIFICATION_CATEGORIES.map((cat, idx) => {
              const on = !!prefs[cat.key];
              const isSaving = saving === cat.key;
              const justSaved = savedKey === cat.key;
              return (
                <React.Fragment key={cat.key}>
                  {idx > 0 && <Divider />}
                  <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 2,
                    px: 2.5, py: 1,
                    bgcolor: justSaved ? alpha(theme.palette.success.main, 0.05) : 'transparent',
                    transition: 'background-color 0.4s',
                  }}>
                    <Box flex={1} minWidth={0}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography variant="body2" fontWeight={600}>{cat.label}</Typography>
                        {justSaved && (
                          <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {cat.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {cat.examples.map(ex => (
                          <Chip
                            key={ex} label={ex} size="small" variant="outlined"
                            sx={{ height: 18, fontSize: '0.85rem', color: 'text.secondary', borderColor: 'divider', borderRadius: 0.75 }}
                          />
                        ))}
                      </Box>
                    </Box>
                    {isSaving ? (
                      <CircularProgress size={20} sx={{ flexShrink: 0, mt: 0.5 }} />
                    ) : (
                      <Switch
                        size="small"
                        checked={on}
                        onChange={e => patch(cat.key, e.target.checked)}
                        color="primary"
                        sx={{ flexShrink: 0 }}
                      />
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
            <Divider />
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 2, px: 2.5, py: 1,
              bgcolor: savedKey === 'email_notify_calls' ? alpha(theme.palette.success.main, 0.05) : 'transparent',
              transition: 'background-color 0.4s',
            }}>
              <Box flex={1} minWidth={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="body2" fontWeight={600}>Funding calls</Typography>
                  {savedKey === 'email_notify_calls' && (
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Occasional emails with up to 5 funding calls picked for your research areas and skills,
                  and updates (deadlines, budgets, funder announcements) to calls you liked or have a
                  proposal for.
                </Typography>
              </Box>
              {saving === 'email_notify_calls' ? (
                <CircularProgress size={20} sx={{ flexShrink: 0, mt: 0.5 }} />
              ) : (
                <Switch
                  size="small"
                  checked={prefs.email_notify_calls !== 'off'}
                  onChange={e => patch('email_notify_calls', e.target.checked ? 'weekly' : 'off')}
                  color="primary"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Box>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};


const PROVIDER_ICONS = {
  google: '🔵',
  github: '⚫',
  orcid: '🟢',
};

const LinkedAccountsSection = () => {
  const theme = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axiosService.get('/auth/social-accounts/');
      setAccounts(res.data || []);
    } catch {
      setError('Failed to load linked accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleUnlink = async (account) => {
    setUnlinking(account.id);
    setError(null);
    setSuccess(null);
    try {
      await axiosService.delete(`/auth/social-accounts/${account.id}/`);
      setSuccess(`${account.provider.charAt(0).toUpperCase() + account.provider.slice(1)} account unlinked.`);
      setAccounts(prev => prev.filter(a => a.id !== account.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to unlink account.');
    } finally {
      setUnlinking(null);
    }
  };

  const handleLink = (provider) => {
    const url = getLinkOAuthUrl(provider);
    if (url) window.location.href = url;
  };

  // Group accounts by provider so we can show multiple per provider
  const accountsByProvider = {};
  accounts.forEach(a => {
    if (!accountsByProvider[a.provider]) accountsByProvider[a.provider] = [];
    accountsByProvider[a.provider].push(a);
  });

  return (
    <Box sx={{ mt: -7, mb: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* <BackButton position="inline" sx={{ mt: 0.5 }} /> */}
        <LinkIcon color="primary" /> Linked Accounts
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Connect your account to third-party providers for quick sign-in
      </Typography>

      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setError(null)}>{error}</Alert>
      </Collapse>
      <Collapse in={!!success}>
        <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setSuccess(null)}>{success}</Alert>
      </Collapse>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          Object.keys(OAUTH_PROVIDERS).map((provider, idx, arr) => {
            const config = OAUTH_PROVIDERS[provider];
            const linked = accountsByProvider[provider] || [];
            return (
              <Box key={provider}>
                {/* Provider header row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40, height: 40, borderRadius: 1.5, fontSize: 20,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {PROVIDER_ICONS[provider] || '🔗'}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{config.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {linked.length === 0 ? 'No accounts linked' : `${linked.length} account${linked.length > 1 ? 's' : ''} linked`}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!config.clientId}
                    onClick={() => handleLink(provider)}
                  >
                    {config.clientId ? '+ Link account' : 'Not configured'}
                  </Button>
                </Box>

                {/* Linked account rows */}
                {linked.length > 0 && (
                  <Stack spacing={0.5} sx={{ px: 3, pb: 2 }}>
                    {linked.map((acct) => (
                      <Box
                        key={acct.id}
                        sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          px: 2, py: 1.25, borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.12),
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {acct.email || 'Linked account'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Linked {new Date(acct.linked_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={unlinking === acct.id}
                          startIcon={unlinking === acct.id ? <CircularProgress size={14} color="inherit" /> : null}
                          onClick={() => handleUnlink(acct)}
                        >
                          {unlinking === acct.id ? 'Unlinking…' : 'Unlink'}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                )}

                {idx < arr.length - 1 && <Divider />}
              </Box>
            );
          })
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Merge with existing account</Typography>
            <Typography variant="caption" color="text.secondary">
              Combine this account with another Otable account you already own
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<MergeTypeIcon />}
            onClick={() => setMergeOpen(true)}
          >
            Merge accounts
          </Button>
        </Box>
      </Paper>

      <MergeAccountDialog
        open={mergeOpen}
        onMerged={() => {
          window.location.replace('/settings/?section=linked-accounts');
        }}
        onDismiss={() => setMergeOpen(false)}
      />
    </Box>
  );
};


const PENDING_STATUS_LABEL = {
  pending: 'Awaiting bank transfer',
  transfer_submitted: 'Transfer submitted — pending confirmation',
};

const BillingSection = () => {
  const { balance, loading: balanceLoading, refresh, accountInfo } = useBillingBalance();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [pendingPurchases, setPendingPurchases] = useState([]);

  const loadAll = useCallback(() => {
    setTxLoading(true);
    Promise.all([
      getTransactions(),
      getPendingPurchases(),
    ]).then(([txRes, ppRes]) => {
      if (txRes.ok) setTransactions(txRes.data);
      if (ppRes.ok) setPendingPurchases(ppRes.data);
      setTxLoading(false);
    });
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const hasVerifiedOrg = accountInfo?.has_verified_org ?? false;
  const hasWelcomeBonus = accountInfo?.has_welcome_bonus ?? false;
  const totalUsed = accountInfo?.total_used ?? 0;
  const totalReceived = accountInfo?.total_received ?? 0;

  return (
    <Box sx={{ mt: -7, mb: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreditCardIcon color="primary" /> Billing & Credits
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        AI writing credits used for proposal section generation.
      </Typography>

      {/* Balance card */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block">
              Current Balance
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
              <BoltIcon sx={{ color: balance === 0 ? 'error.main' : 'primary.main', fontSize: 32 }} />
              {balanceLoading ? (
                <CircularProgress size={28} />
              ) : (
                <Typography variant="h3" fontWeight={800} color={balance === 0 ? 'error.main' : 'text.primary'}>
                  {balance ?? 0}
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary" sx={{ pt: 1 }}>
                credits
              </Typography>
            </Stack>

            {/* Context under the balance number */}
            {!balanceLoading && (
              <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                {totalReceived > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Received: <strong>{totalReceived}</strong>
                  </Typography>
                )}
                {totalUsed > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Used: <strong>{totalUsed}</strong>
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddCardIcon />}
            onClick={() => setPaymentOpen(true)}
            sx={{ minWidth: 160 }}
            disabled
          >
            Add Credits
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Verified org / welcome bonus status */}
        {!balanceLoading && (
          hasWelcomeBonus ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <VerifiedUserIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="body2" color="success.main">
                You received 2 welcome credits when your organization membership was verified.
              </Typography>
            </Stack>
          ) : hasVerifiedOrg ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">
                Your organization is verified — welcome credits will be applied shortly.
              </Typography>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Get <strong>2 free credits</strong> when your organization membership is verified by an admin.
              </Typography>
            </Stack>
          )
        )}
      </Paper>

      {/* Pending bank transfers */}
      {pendingPurchases.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: 'warning.light', bgcolor: 'warning.50' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon fontSize="small" color="warning" />
            Pending Bank Transfer{pendingPurchases.length > 1 ? 's' : ''}
          </Typography>
          <Stack spacing={1.5}>
            {pendingPurchases.map(pp => (
              <Stack key={pp.id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                    {pp.payment_intent_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {PENDING_STATUS_LABEL[pp.status] || pp.status}
                  </Typography>
                </Box>
                <Chip
                  label={`+${pp.credits} credits`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* How credits work */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.light' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <AutoAwesomeIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>
              How credits work
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                1 credit is deducted each time you generate an AI-written section draft.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Credits are shared across all your proposals.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                New members with a verified organization membership receive <strong>2 free welcome credits</strong>.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Additional credits are purchased via <strong>bank transfer</strong> (EUR / USD). Credits are added after payment is confirmed by the platform team.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Transaction history */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Transaction History
        </Typography>
        <Chip
          icon={<AccountBalanceIcon />}
          label="Bank transfer payments only"
          size="small"
          variant="outlined"
          color="default"
        />
      </Stack>
      <TransactionHistory transactions={transactions} loading={txLoading} />

      {/* <BillingLegalSection /> */}

      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        productName="AI Writing Credits"
        productDescription="Credits for generating proposal section drafts using GPT-4o."
        unitLabel="credits"
        costPerOperation={1}
        operationLabel="section generation"
        currentBalance={balance ?? 0}
        onSuccess={() => {
          refresh();
          setPaymentOpen(false);
          loadAll();
        }}
      />
    </Box>
  );
};


// ── Appearance Section ─────────────────────────────────────────────────────────
const AppearanceSection = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = React.useContext(ColorModeContext);
  const isDark = mode === 'dark';

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Appearance</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how Otable looks to you.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  display: 'flex',
                }}
              >
                {isDark ? <DarkModeIcon /> : <LightModeIcon />}
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {isDark ? 'Dark mode' : 'Light mode'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isDark
                    ? 'Using dark theme — easier on the eyes in low light.'
                    : 'Using light theme — optimal for bright environments.'}
                </Typography>
              </Box>
            </Stack>
            <Switch
              checked={isDark}
              onChange={toggleColorMode}
              color="primary"
              inputProps={{ 'aria-label': 'Toggle dark mode' }}
            />
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
          {/* Light preview */}
          <Box
            onClick={isDark ? toggleColorMode : undefined}
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: 'hidden',
              border: '2px solid',
              borderColor: !isDark ? 'primary.main' : 'divider',
              cursor: isDark ? 'pointer' : 'default',
              transition: 'border-color 0.2s',
              '&:hover': isDark ? { borderColor: 'primary.light' } : {},
            }}
          >
            <Box sx={{ bgcolor: '#f8f9fa', p: 1.5 }}>
              <Box sx={{ height: 8, width: '60%', bgcolor: '#6366f1', borderRadius: 1, mb: 1 }} />
              <Box sx={{ height: 6, width: '80%', bgcolor: '#e0e7ff', borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ height: 6, width: '50%', bgcolor: '#e0e7ff', borderRadius: 1 }} />
            </Box>
            <Box sx={{ bgcolor: '#ffffff', p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!isDark && <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
              <Typography variant="caption" fontWeight={600} color={!isDark ? 'primary.main' : 'text.secondary'}>
                Light
              </Typography>
            </Box>
          </Box>

          {/* Dark preview */}
          <Box
            onClick={!isDark ? toggleColorMode : undefined}
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: 'hidden',
              border: '2px solid',
              borderColor: isDark ? 'primary.main' : 'divider',
              cursor: !isDark ? 'pointer' : 'default',
              transition: 'border-color 0.2s',
              '&:hover': !isDark ? { borderColor: 'primary.light' } : {},
            }}
          >
            <Box sx={{ bgcolor: '#1e1e2e', p: 1.5 }}>
              <Box sx={{ height: 8, width: '60%', bgcolor: '#818cf8', borderRadius: 1, mb: 1 }} />
              <Box sx={{ height: 6, width: '80%', bgcolor: '#252535', borderRadius: 1, mb: 0.5 }} />
              <Box sx={{ height: 6, width: '50%', bgcolor: '#252535', borderRadius: 1 }} />
            </Box>
            <Box sx={{ bgcolor: '#16213e', p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isDark && <CheckCircleIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
              <Typography variant="caption" fontWeight={600} color={isDark ? 'primary.main' : 'text.secondary'}>
                Dark
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};


const DATE_FORMAT_OPTIONS = [
  { value: 'dmy', label: 'DD/MM/YYYY', example: '28/06/2026' },
  { value: 'mdy', label: 'MM/DD/YYYY', example: '06/28/2026' },
  { value: 'ymd', label: 'YYYY-MM-DD', example: '2026-06-28' },
];

const NUMBER_LOCALE_OPTIONS = [
  { value: 'eu', label: 'European',  example: '1.234.567,89' },
  { value: 'us', label: 'US / UK',   example: '1,234,567.89' },
];

const LocalizationSection = () => {
  const theme = useTheme();
  const { formatDate, formatNumber } = useLocale();
  const [dateFmt, setDateFmt] = useState(null);
  const [numLoc, setNumLoc] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axiosService.get('/users/privacy-settings/').then(res => {
      setDateFmt(res.data.date_format || 'dmy');
      setNumLoc(res.data.number_locale || 'eu');
    }).catch(() => {});
  }, []);

  const save = async (field, value) => {
    setSaving(field);
    setError(null);
    setSuccess(false);
    try {
      await axiosService.patch('/users/privacy-settings/', { [field]: value });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const handleDate = (value) => { setDateFmt(value); save('date_format', value); };
  const handleNum  = (value) => { setNumLoc(value);  save('number_locale', value); };

  const now = new Date();

  return (
    <Box sx={{ mt: -7, mb: 2 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageIcon color="primary" /> Localization
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Choose how dates and numbers are displayed across the platform.
      </Typography>

      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      </Collapse>
      <Collapse in={success}>
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>Saved.</Alert>
      </Collapse>

      <Stack spacing={2}>
        {/* Date format */}
        <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
              <ScheduleIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>Date Format</Typography>
              <Typography variant="body2" color="text.secondary">
                Preview: <strong>{dateFmt ? formatDate(now, 'short') : '…'}</strong>
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {DATE_FORMAT_OPTIONS.map(opt => (
              <Paper
                key={opt.value}
                variant="outlined"
                onClick={() => handleDate(opt.value)}
                sx={{
                  px: 2, py: 1.5, cursor: 'pointer', borderRadius: 1.5, minWidth: 140,
                  borderColor: dateFmt === opt.value ? 'primary.main' : 'divider',
                  bgcolor: dateFmt === opt.value ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                  position: 'relative',
                }}
              >
                {saving === 'date_format' && dateFmt === opt.value && (
                  <CircularProgress size={12} sx={{ position: 'absolute', top: 6, right: 6 }} />
                )}
                <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.example}</Typography>
              </Paper>
            ))}
          </Stack>
        </Paper>

        {/* Number format */}
        <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
              <BoltIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>Number Format</Typography>
              <Typography variant="body2" color="text.secondary">
                Preview: <strong>{numLoc ? formatNumber(1234567.89, { minimumFractionDigits: 2 }) : '…'}</strong>
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {NUMBER_LOCALE_OPTIONS.map(opt => (
              <Paper
                key={opt.value}
                variant="outlined"
                onClick={() => handleNum(opt.value)}
                sx={{
                  px: 2, py: 1.5, cursor: 'pointer', borderRadius: 1.5, minWidth: 160,
                  borderColor: numLoc === opt.value ? 'primary.main' : 'divider',
                  bgcolor: numLoc === opt.value ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main' },
                  position: 'relative',
                }}
              >
                {saving === 'number_locale' && numLoc === opt.value && (
                  <CircularProgress size={12} sx={{ position: 'absolute', top: 6, right: 6 }} />
                )}
                <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.example}</Typography>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState('account');

  // Support ?section=<id> query param (e.g. after OAuth link redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) setActiveSection(section);
  }, []);

  const sections = [
    { id: 'account',         label: 'Account Settings', icon: <PersonIcon />         },
    { id: 'privacy',         label: 'Privacy Settings', icon: <PrivacyIcon />        },
    { id: 'notifications',   label: 'Notifications',    icon: <NotificationsIcon />  },
    { id: 'linked-accounts', label: 'Linked Accounts',  icon: <LinkIcon />           },
    // { id: 'appearance',   label: 'Appearance',        icon: <PaletteIcon />        },
    { id: 'terms',   label: 'Terms & Policies',   icon: <TermsIcon />    },
    { id: 'contact', label: 'Contact & Support',  icon: <ContactIcon />  },
    { id: 'billing',       label: 'Billing & Credits',  icon: <CreditCardIcon /> },
    { id: 'storage',       label: 'Storage',             icon: <StorageIcon />    },
    { id: 'localization',  label: 'Localization',        icon: <LanguageIcon />   },
  ];

  const renderSection = () => {
    switch (activeSection) {

      case 'account':
        return <AccountSection />;

      case 'privacy':
        return <PrivacySection />;

      case 'notifications':
        return <NotificationsSection />;

      case 'linked-accounts':
        return <LinkedAccountsSection />;
      case 'appearance':
        return <AppearanceSection />;
      case 'terms':
        return <TermsSection />;
      case 'contact':
        return <ContactSection />;
      case 'billing':
        return <BillingSection />;
      case 'storage':
        return <StorageSection />;
      case 'localization':
        return <LocalizationSection />;
      default:
        return <PrivacySection />;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <PageContainer>
        <PageHeader title="Settings" />

        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Fixed Sidebar */}
          <Paper 
            elevation={0} 
            sx={{ 
            
              width: 280, 
              flexShrink: 0,
              position: 'sticky',
              top: 24,
              alignSelf: 'flex-start',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >

            <List disablePadding>
              {sections.map((section) => (
                <SidebarItem
                  key={section.id}
                  icon={section.icon}
                  text={section.label}
                  active={activeSection === section.id}
                  badge={section.badge}
                  onClick={() => setActiveSection(section.id)}
                />
              ))}
            </List>
            
            {/* <Divider sx={{ my: 2 }} /> */}
            
            {/* <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quick Actions
              </Typography>
              <Button
                fullWidth
                startIcon={<SecurityIcon />}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Security Checkup
              </Button>
              <Button
                fullWidth
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Export Data
              </Button>
            </Box> */}
          </Paper>

          {/* Main Content Area */}
          <Box sx={{ flex: 1 }}>

            {renderSection()}
            
            {/* No Save Button needed for privacy - auto-saves on change */}
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
};

// Import missing DownloadIcon


export default SettingsPage;